import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import PopUp from '../../../ui/PopUp/PopUp'
import DataTable from '../../../ui/DataTable/DataTable'
import Spinner from '../../../ui/Spinner/Spinner'
import EmptyData from '../../../ui/EmptyData/EmptyData'
import '../../../ui/ActiveStatusBadge/ActiveStatusBadge.sass'
import Price from '../../Products/components/ProductsList/Price'
import { formatDateAsDayMontYearHHMM } from '../../../utils/date/dateFormat'
import listAmeCampaignPromoCodes from '../../../api/marketing/ameCampaigns/listAmeCampaignPromoCodes'
import { FilterGroup } from '../Gifts/GiftsFundTableFilters'
import styles from './AmeCampaigns.module.scss'
import { AME_ISSUE_KIND_BY_PHRASE } from '../../../api/marketing/ameCampaigns/ameCampaignConstants'

const FILTER_ALL = 'all'
const FILTER_USED = 'used'

const FILTER_OPTIONS = [
  { value: FILTER_ALL, label: 'Усі' },
  { value: FILTER_USED, label: 'Використані' },
]

const PAGE_LIMIT = 25

const toSecondsStamp = (iso) => {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return null
  return { seconds: Math.floor(ms / 1000) }
}

const formatStamp = (iso) => {
  const stamp = toSecondsStamp(iso)
  if (!stamp) return '—'
  return formatDateAsDayMontYearHHMM(stamp)
}

function AmeCampaignPromoCodesPopUp({ campaign, initialFilter = FILTER_ALL, onClose }) {
  const campaignId = campaign?.id || null
  const isPhrase = campaign?.issue?.kind === AME_ISSUE_KIND_BY_PHRASE
  const title = `${isPhrase ? 'Використання фрази' : 'Промокоди'}${campaign?.title ? ` · ${campaign.title}` : ''}`

  const [filter, setFilter] = useState(initialFilter === FILTER_USED ? FILTER_USED : FILTER_ALL)
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setFilter(initialFilter === FILTER_USED ? FILTER_USED : FILTER_ALL)
  }, [initialFilter, campaignId])

  const loadPage = useCallback(
    async ({ nextFilter = filter } = {}) => {
      if (!campaignId) return
      setIsLoading(true)
      setError('')
      setRows([])

      const collected = []
      let cursor = null
      let guard = 0

      while (guard < 40) {
        guard += 1
        const result = await listAmeCampaignPromoCodes({
          campaignId,
          filter: nextFilter,
          limit: PAGE_LIMIT,
          cursor: cursor == null ? undefined : cursor,
        })

        if (!result.ok) {
          setRows([])
          setError(result.message || 'Не вдалося завантажити промокоди')
          setIsLoading(false)
          return
        }

        const items = Array.isArray(result.data?.items) ? result.data.items : []
        items.forEach((item, index) => {
          collected.push({
            id: `${item.codeNormalized || 'code'}-${item.uid || index}-${collected.length}`,
            ...item,
          })
        })

        const next = result.data?.nextCursor ?? null
        if (next == null) break
        cursor = next
      }

      setRows(collected)
      setIsLoading(false)
    },
    [campaignId, filter]
  )

  useEffect(() => {
    if (!campaignId) return undefined
    loadPage({ nextFilter: filter })
    return undefined
  }, [campaignId, filter, loadPage])

  const columns = useMemo(
    () =>
      [
        {
          field: 'issuedAt',
          headerClassName: 'TableHeadCol',
          headerName: 'Дата видачі',
          width: 180,
          renderCell: (params) => formatStamp(params.row.issuedAt),
        },
        {
          field: 'codeNormalized',
          headerClassName: 'TableHeadCol',
          headerName: isPhrase ? 'Кодова фраза' : 'Промокод',
          width: isPhrase ? 230 : 150,
          renderCell: (params) => (
            <span className={styles.codeCell}>{params.row.phrase || params.row.codeNormalized || '—'}</span>
          ),
        },
        {
          field: 'customerName',
          headerClassName: 'TableHeadCol',
          headerName: 'Клієнт',
          minWidth: 200,
          flex: 1,
          renderCell: (params) => params.row.customerName || '—',
        },
        {
          field: 'statusLabel',
          headerClassName: 'TableHeadCol',
          headerName: 'Статус',
          width: 140,
          renderCell: (params) => {
            const availability = params.row.availability
            const label = params.row.statusLabel || '—'
            if (availability === 'available') {
              return <span className="ActiveStatusBadge ActiveStatusBadge_active">{label}</span>
            }
            if (availability === 'used' || availability === 'unavailable') {
              return <span className="ActiveStatusBadge ActiveStatusBadge_inactive">{label}</span>
            }
            return label
          },
        },
        {
          field: 'usedAt',
          headerClassName: 'TableHeadCol',
          headerName: 'Використано',
          width: 180,
          renderCell: (params) => formatStamp(params.row.usedAt),
        },
        {
          field: 'orderNum',
          headerClassName: 'TableHeadCol',
          headerName: 'Замовлення',
          width: 130,
          renderCell: (params) => {
            if (!params.row.orderId) return '—'
            const label = params.row.orderNum != null ? String(params.row.orderNum) : params.row.orderId
            return (
              <Link className={styles.tableLink} to={`/orders/list/${params.row.orderId}`}>
                {label}
              </Link>
            )
          },
        },
        {
          field: 'discountTotal',
          headerClassName: 'TableHeadCol',
          headerName: 'Сума знижки',
          width: 140,
          renderCell: (params) => {
            if (params.row.discountTotal == null) return '—'
            return <Price num={params.row.discountTotal} />
          },
        },
      ].filter((column) => !isPhrase || column.field !== 'issuedAt'),
    [isPhrase]
  )

  return (
    <PopUp show={Boolean(campaignId)} title={title} close={onClose} className="AmeCampaignCodesPopUp">
      <div className={styles.codesPopUpBody}>
        {!isPhrase ? (
          <FilterGroup
            label="Статус"
            value={filter}
            options={FILTER_OPTIONS}
            onChange={(next) => {
              setFilter(next)
              setRows([])
            }}
          />
        ) : null}

        {error ? <p className={styles.codesError}>{error}</p> : null}

        {isLoading && !rows.length ? (
          <div className={styles.codesState}>
            <Spinner type="module" />
          </div>
        ) : null}

        {!isLoading && !error && !rows.length ? (
          <div className={styles.codesState}>
            <EmptyData title={isPhrase ? 'Промокод ще не використовували' : 'Промокодів не знайдено'} />
          </div>
        ) : null}

        {rows.length ? (
          <div className={styles.codesTable}>
            <DataTable
              table={{
                rows,
                columns,
                autoHeight: true,
                disableRowSelectionOnClick: true,
              }}
            />
          </div>
        ) : null}
      </div>
    </PopUp>
  )
}

export default AmeCampaignPromoCodesPopUp
export { FILTER_ALL, FILTER_USED }
